import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/api';
import EmploymentStatus from '../../components/admin/EmploymentStatus';
import LifecycleModal from '../../components/admin/LifecycleModal';
import { loadAssignmentPolicy, localActionsAllowed } from '../../utils/assignmentPolicy';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import SupportMirrorButton from '../../components/support/SupportMirrorButton';
import Modal from '../../components/common/Modal';
import PlacementHistory from '../../components/admin/PlacementHistory';
import {
  FiEdit,
  FiSearch,
  FiInfo,
  FiCheck,
  FiX,
  FiPackage,
  FiPlus,
  FiUploadCloud,
  FiMail,
  FiCopy,
} from 'react-icons/fi';
import { LANGUAGES, languagesForRole } from '../../i18n/languages';
import { getPlacements, isCrmSynced, placementIsCurrent } from '../../utils/adminV2';
import { useEmailBatch, describeDelivery, deliveryCounts } from '../../hooks/useEmailBatch';

// Colours for the per-person delivery status in the invitation dialog.
const DELIVERY_COLORS = { ok: '#1e8449', warn: '#b9770e', bad: 'var(--act-danger)' };

// Styled components
const PageContainer = styled.div`
  width: 100%; min-width: 0;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
  gap: ${({ theme }) => theme.spacing[2]};
  @media (max-width: 768px) {
    width: 100%;
    > button { flex: 1 1 150px; }
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  margin-left: auto;
  @media (max-width: 576px) { flex: 1 1 100%; max-width: none; margin-left: 0; }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[8]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[2]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Table styles
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%; min-width: 1450px;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  
  ${({ status, theme }) => {
    if (status === true) {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (status === false) {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const SyncBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing[1]};
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.info};
  background: ${({ theme }) => `${theme.colors.info}15`};
`;

const ActionButton = styled(Button)`
  padding: 6px;
  margin-right: 4px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const PaginationInfo = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const AlertMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  ${({ type, theme }) => {
    if (type === 'success') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (type === 'error') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const DetailTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const CustomerChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  margin: 0 4px 4px 0;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    cursor: not-allowed;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const CustomerList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const CustomerCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  input {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const FormContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[2]};
`;

const Hint = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const PlacementNote = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  border-left: 3px solid ${({ theme }) => theme.colors.info};
  background: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    isActive: '',
    danlonActive: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  const [alert, setAlert] = useState(null);
  const [customers, setCustomers] = useState([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState(null);
  // Employee status is managed in CRM unless the local actions switch is on
  // (1.70.0). Read-only until the policy is known.
  const [assignmentPolicy, setAssignmentPolicy] = useState(null);
  const localActions = localActionsAllowed(assignmentPolicy);
  const [showAssignCustomersModal, setShowAssignCustomersModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [assignError, setAssignError] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const danlonFileRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [inviting, setInviting] = useState(false);
  const [inviteReport, setInviteReport] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const navigate = useNavigate();
  // Invitations queue behind the mail limit (v1.42.0): follow the batch so the
  // dialog shows "Sent 09:41" / "Failed: …" per person, not a hopeful "e-mailed".
  // Only when something was queued: a batch with no rows does not exist.
  const inviteBatchId = (inviteReport && inviteReport.queued > 0 && inviteReport.batchId) || null;
  const { batch: inviteBatch } = useEmailBatch(inviteBatchId, { enabled: Boolean(inviteBatchId) });
  const inviteCounts = deliveryCounts(inviteReport && inviteReport.results, inviteBatch);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const allSelected = employees.length > 0 && employees.every((e) => selectedIds.has(e.id));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(employees.map((e) => e.id)));
  };

  const handleSendInvitations = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Send invitations to ${selectedIds.size} selected employee(s)? Active accounts with a real email address receive a personal login link. Existing working links and sessions are preserved.`)) return;
    try {
      setInviting(true);
      setAlert(null);
      const report = await adminService.sendInvitations([...selectedIds]);
      setInviteReport(report);
      setSelectedIds(new Set());
      fetchEmployees();
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Failed to send invitations.' });
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLoginLink = (link, idx) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(null), 2000);
      }).catch(() => {});
    }
  };

  const handleImportDanlon = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      setImporting(true);
      setAlert(null);
      const report = await adminService.importDanlonEmployees(file);
      setImportReport(report);
      fetchEmployees();
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Failed to import the Danløn Excel.' });
    } finally {
      setImporting(false);
    }
  };

  // Fetch customers for assignment once on mount. Use a high limit so the
  // checkbox list shows every active customer, not just the first default page.
  useEffect(() => {
    adminService.getCustomers({ isActive: true, limit: 1000, page: 1 })
      .then(data => setCustomers(data.customers || []))
      .catch(error => console.error('Error fetching customers:', error));
  }, []);

  useEffect(() => {
    let active = true;
    loadAssignmentPolicy().then((policy) => { if (active) setAssignmentPolicy(policy); });
    return () => { active = false; };
  }, []);

  // Fetch the employee list whenever filters or page change
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);

      // Only send non-empty filters; convert 'true'/'false' to boolean for isActive
      const params = {
        page: pagination.page,
        ...(filters.search && { search: filters.search }),
        ...(filters.isActive !== '' && { isActive: filters.isActive === 'true' }),
        ...(filters.danlonActive !== '' && { danlonActive: filters.danlonActive === 'true' })
      };

      const response = await adminService.getEmployees(params);
      
      setEmployees(response.employees);
      setPagination({
        page: response.page,
        totalPages: response.totalPages,
        total: response.total
      });
    } catch (error) {
      console.error('Error fetching employees:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load employees. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    // Reset to first page when changing filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    // Debounce search input
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      setPagination(prev => ({ ...prev, page: prev.page - 1 }));
    }
  };

  const handleActivateEmployee = (employeeId) => {
    setLifecycleAction({ user: employees.find((employee) => employee.id === employeeId), action: 'activate' });
  };

  const handleDeactivateEmployee = (employeeId) => {
    setLifecycleAction({ user: employees.find((employee) => employee.id === employeeId), action: 'deactivate' });
  };

  const handleUpdateEmployee = async (employeeData) => {
    try {
      const emailChanged = String(employeeData.email || '').trim().toLowerCase()
        !== String(selectedEmployee.email || '').trim().toLowerCase();
      await adminService.updateEmployee(selectedEmployee.id, employeeData);
      if (emailChanged) {
        setSelectedIds((previous) => new Set(previous).add(selectedEmployee.id));
      }
      setShowEditModal(false);
      setAlert({
        type: 'success',
        message: emailChanged
          ? 'Employee updated successfully. The employee is selected; send a new invitation for the changed login e-mail.'
          : 'Employee updated successfully.'
      });
      fetchEmployees();
    } catch (error) {
      console.error('Error updating employee:', error);
      setAlert({
        type: 'error',
        message: error.message || 'Failed to update employee. Please try again.'
      });
    }
  };

  const handleCreateEmployee = async (employeeData) => {
    try {
      const created = await adminService.createEmployee(employeeData);
      setShowAddModal(false);
      setAlert({
        type: 'success',
        message: 'Employee created successfully.'
      });
      setCreatedInfo({
        name: created ? `${created.firstName || ''} ${created.lastName || ''}`.trim() : ''
      });
      fetchEmployees();
    } catch (error) {
      console.error('Error creating employee:', error);
      setAlert({
        type: 'error',
        message: error.message || 'Failed to create employee. Please try again.'
      });
    }
  };

  const handleViewEmployeeDetails = async (employee) => {
    try {
      const employeeDetails = await adminService.getEmployeeById(employee.id);
      setSelectedEmployee(employeeDetails);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load employee details. Please try again.'
      });
    }
  };

  const handleOpenAssignCustomers = async (employee) => {
    try {
      const employeeDetails = await adminService.getEmployeeById(employee.id);
      setSelectedEmployee(employeeDetails);
      const assigned = employeeDetails.assignedCustomers || [];
      const currentPlacement = getPlacements(employeeDetails).find((placement) => placementIsCurrent(placement));
      setSelectedCustomerId(assigned.length > 0 ? assigned[0].id : (currentPlacement?.customerId || ''));
      setAssignError(null);
      setShowAssignCustomersModal(true);
    } catch (error) {
      console.error('Error preparing customer assignment:', error);
      setAlert({
        type: 'error',
        message: 'Failed to prepare customer assignment. Please try again.'
      });
    }
  };

  const handleAssignCustomers = async () => {
    try {
      setAssignError(null);
      const currentPlacement = getPlacements(selectedEmployee).find((placement) => placementIsCurrent(placement));
      const currentCustomerId = selectedEmployee.assignedCustomers?.[0]?.id || currentPlacement?.customerId || '';
      if (String(currentCustomerId) !== String(selectedCustomerId)) {
        const destination = customers.find((customer) => String(customer.id) === String(selectedCustomerId));
        const confirmed = window.confirm(
          selectedCustomerId
            ? `Reassign ${selectedEmployee.firstName} ${selectedEmployee.lastName} to ${destination?.name || 'the selected customer'}? `
              + 'The current placement will be ended and a new placement will start; the previous placement remains in history.'
            : `End the current placement for ${selectedEmployee.firstName} ${selectedEmployee.lastName}? `
              + 'The employee becomes unassigned, but the placement remains in history.'
        );
        if (!confirmed) return;
      }
      // One-customer rule: the backend allows at most one assigned customer.
      await adminService.assignEmployeeToCustomers(
        selectedEmployee.id,
        selectedCustomerId ? [selectedCustomerId] : []
      );
      setShowAssignCustomersModal(false);
      setAlert({
        type: 'success',
        message: 'Customer assignment updated successfully.'
      });
      fetchEmployees();
    } catch (error) {
      console.error('Error assigning customer:', error);
      // Surface ONE_CUSTOMER_ONLY / EMPLOYEE_ALREADY_ASSIGNED messages in the modal
      setAssignError(error.message || 'Failed to assign the customer. Please try again.');
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Employee Management</PageTitle>
        <HeaderActions>
          <input
            ref={danlonFileRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={handleImportDanlon}
          />
          {localActions && <Button variant="outline" onClick={() => danlonFileRef.current && danlonFileRef.current.click()} disabled={importing}>
            <FiUploadCloud style={{ marginRight: '8px' }} />
            {importing ? 'Importing…' : 'Import Danløn Excel'}
          </Button>}
          <Button variant="outline" onClick={handleSendInvitations} disabled={inviting || selectedIds.size === 0}>
            <FiMail style={{ marginRight: '8px' }} />
            {inviting ? 'Sending…' : `Send Invitations${selectedIds.size ? ` (${selectedIds.size})` : ''}`}
          </Button>
          {localActions && <Button onClick={() => setShowAddModal(true)}>
            <FiPlus style={{ marginRight: '8px' }} />
            Add Employee
          </Button>}
        </HeaderActions>
      </PageHeader>
      
      <Card padding="md">
        {alert && (
          <AlertMessage type={alert.type}>
            {alert.message}
          </AlertMessage>
        )}
        
        <FiltersContainer>
          <FilterGroup>
            <FilterLabel htmlFor="isActive">Tempus status</FilterLabel>
            <FilterSelect
              id="isActive"
              name="isActive"
              value={filters.isActive}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="danlonActive">Danløn status</FilterLabel>
            <FilterSelect
              id="danlonActive"
              name="danlonActive"
              value={filters.danlonActive}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </FilterSelect>
          </FilterGroup>

          <SearchContainer>
            <SearchIcon>
              <FiSearch />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by name or email..."
              defaultValue={filters.search}
              onChange={handleSearchChange}
            />
          </SearchContainer>
        </FiltersContainer>
        
        {loading ? (
          <EmptyState>Loading employees...</EmptyState>
        ) : (
          <>
            <p>Employment follows customer placement dates. Status reflects today; start and end show the latest employment. Account access is shown separately. Manage employment dates and status in CRM.</p>
            <TableContainer tabIndex={0} role="region" aria-label="Employee list; scroll horizontally for all columns">
              <Table>
                <TableHead>
                  <tr>
                    <TableHeadCell style={{ width: '32px' }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title="Select all on this page" />
                    </TableHeadCell>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Danløn Medarb. nr.</TableHeadCell>
                    <TableHeadCell>Email</TableHeadCell>
                    <TableHeadCell>CPR</TableHeadCell>
                    <TableHeadCell>EUR</TableHeadCell>
                    <TableHeadCell>Employment status</TableHeadCell>
                    <TableHeadCell>Employment Start</TableHeadCell>
                    <TableHeadCell>Employment End</TableHeadCell>
                    <TableHeadCell>Tempus status</TableHeadCell>
                    <TableHeadCell>Danløn status</TableHeadCell>
                    <TableHeadCell>Last Login</TableHeadCell>
                    <TableHeadCell>Assigned Customers</TableHeadCell>
                    <TableHeadCell>Actions</TableHeadCell>
                  </tr>
                </TableHead>
                <tbody>
                  {employees.length > 0 ? (
                    employees.map(employee => (
                      <TableRow key={employee.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(employee.id)}
                            onChange={() => toggleSelect(employee.id)}
                            aria-label={`Select ${employee.firstName} ${employee.lastName}`}
                          />
                        </TableCell>
                        <TableCell>{employee.firstName} {employee.lastName}</TableCell>
                        <TableCell>{employee.employeeCode || '—'}</TableCell>
                        <TableCell>{employee.email}</TableCell>
                        <TableCell>{employee.cpr || '—'}</TableCell>
                        <TableCell>
                          {employee.hourlyRateEur !== null && employee.hourlyRateEur !== undefined
                            ? Number(employee.hourlyRateEur).toFixed(2)
                            : '—'}
                        </TableCell>
                        <TableCell><EmploymentStatus employee={employee} dates={false} /></TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>{employee.employmentStartDate || '—'}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>{employee.employmentEndDate || (employee.employmentStartDate ? 'Open ended' : '—')}</TableCell>
                        <TableCell>
                          <Badge status={employee.isActive}>
                            {employee.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          {!employee.isActive && isCrmSynced(employee) && (
                            <SyncBadge title="This inactive employee is still linked to VikarCRM master data.">
                              CRM synced
                            </SyncBadge>
                          )}
                        </TableCell>
                        <TableCell>
                          {employee.danlonActive === null || employee.danlonActive === undefined
                            ? '—'
                            : (
                              <Badge status={employee.danlonActive}>
                                {employee.danlonActive ? 'Active' : 'Inactive'}
                              </Badge>
                            )}
                        </TableCell>
                        <TableCell>
                          {employee.lastLogin
                            ? new Date(employee.lastLogin).toLocaleDateString() 
                            : 'Never'}
                        </TableCell>
                        <TableCell>
                          {employee.assignedCustomers && employee.assignedCustomers.length > 0 
                            ? `${employee.assignedCustomers.length} customers` 
                            : 'None'}
                        </TableCell>
                        <TableCell>
                          <ActionButton 
                            variant="icon" 
                            title="View Details"
                            onClick={() => handleViewEmployeeDetails(employee)}
                          >
                            <FiInfo />
                          </ActionButton>
                          <SupportMirrorButton userId={employee.id} name={`${employee.firstName} ${employee.lastName}`} disabled={!employee.isActive} />
                        
                          <ActionButton 
                            variant="icon" 
                            title="Edit"
                            onClick={() => {
                              setSelectedEmployee(employee);
                              setShowEditModal(true);
                            }}
                          >
                            <FiEdit />
                          </ActionButton>
                          
                          {localActions && <ActionButton
                            variant="icon"
                            title="Assign Customer"
                            onClick={() => handleOpenAssignCustomers(employee)}
                          >
                            <FiPackage />
                          </ActionButton>}
                          
                          {localActions && (employee.isActive ? (
                            <ActionButton
                              variant="icon"
                              title="Deactivate"
                              onClick={() => handleDeactivateEmployee(employee.id)}
                            >
                              <FiX />
                            </ActionButton>
                          ) : (
                            <ActionButton 
                              variant="icon" 
                              title="Activate"
                              onClick={() => handleActivateEmployee(employee.id)}
                            >
                              <FiCheck />
                            </ActionButton>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={14} style={{ textAlign: 'center' }}>
                        No employees found matching your filters.
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
            
            <Pagination>
              <PaginationInfo>
                Showing {Math.min((pagination.page - 1) * 10 + 1, pagination.total)} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} employees
              </PaginationInfo>
              <PaginationButtons>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePrevPage}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleNextPage}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </Button>
              </PaginationButtons>
            </Pagination>
          </>
        )}
      </Card>
      
      {lifecycleAction && <LifecycleModal
        user={{ ...lifecycleAction.user, role: 'employee' }} action={lifecycleAction.action}
        onClose={() => setLifecycleAction(null)} onSaved={() => fetchEmployees()}
      />}
      {/* Employee Detail Modal */}
      {showDetailModal && selectedEmployee && (
        <Modal
          title={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
          onClose={() => setShowDetailModal(false)}
          size="lg"
        >
          <DetailSection>
            <EmploymentStatus employee={selectedEmployee} />
            <p><strong>Email:</strong> {selectedEmployee.email}</p>
            <p><strong>CPR:</strong> {selectedEmployee.cpr || '—'}</p>
            <p>
              <strong>Status:</strong> {selectedEmployee.isActive ? 'Active' : 'Inactive'}
              {!selectedEmployee.isActive && isCrmSynced(selectedEmployee) && (
                <SyncBadge title="The account is inactive in Tempus; its VikarCRM master-data link remains visible.">
                  CRM synced
                </SyncBadge>
              )}
            </p>
            <p><strong>Created:</strong> {new Date(selectedEmployee.createdAt).toLocaleDateString()}</p>
            <p><strong>Last Login:</strong> {selectedEmployee.lastLogin ? new Date(selectedEmployee.lastLogin).toLocaleDateString() : 'Never'}</p>
          </DetailSection>

          <DetailSection>
            <DetailTitle>Placement history</DetailTitle>
            <PlacementHistory
              placements={getPlacements(selectedEmployee)}
              customers={selectedEmployee.assignedCustomers || []}
              emptyMessage="No current or historical placements are available for this employee."
            />
          </DetailSection>
          
          <DetailSection>
            <DetailTitle>Current placement</DetailTitle>
            <div>
              {selectedEmployee.assignedCustomers && selectedEmployee.assignedCustomers.length > 0 ? (
                selectedEmployee.assignedCustomers.map(customer => (
                  <CustomerChip key={customer.id}>
                    {customer.name}
                  </CustomerChip>
                ))
              ) : (
                <p>No current placement.</p>
              )}
            </div>
          </DetailSection>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </div>
        </Modal>
      )}
      
      {/* Edit Employee Modal */}
      {showEditModal && selectedEmployee && (
        <Modal
          title="Edit Employee"
          onClose={() => setShowEditModal(false)}
        >
          <FormContainer>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);

              // CPR is the business EmployeeID (Danish CPR / Danløn pay-part id).
              // Optional, but when present it must be exactly 10 digits. Strip any
              // separators (e.g. DDMMYY-XXXX) before validating and submitting.
              const cprInput = (formData.get('cpr') || '').trim();
              const cprDigits = cprInput.replace(/\D/g, '');
              if (cprInput && cprDigits.length !== 10) {
                setAlert({
                  type: 'error',
                  message: 'CPR must be 10 digits (DDMMYY-XXXX).'
                });
                return;
              }

              // Hourly wage in EUR — optional; an empty field clears the rate.
              const hourlyRateInput = (formData.get('hourlyRateEur') || '').trim();

              const data = {
                firstName: formData.get('firstName'),
                lastName: formData.get('lastName'),
                email: formData.get('email'),
                department: formData.get('department'),
                position: formData.get('position'),
                employeeCode: formData.get('employeeCode'),
                ...(localActions ? { startDate: formData.get('startDate') || null } : {}),
                cpr: cprDigits,
                language: formData.get('language') || null,
                hourlyRateEur: hourlyRateInput === '' ? null : Number(hourlyRateInput)
              };
              handleUpdateEmployee(data);
            }}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="edit-firstName">First Name</Label>
                  <Input
                    type="text"
                    id="edit-firstName"
                    name="firstName"
                    defaultValue={selectedEmployee.firstName}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="edit-lastName">Last Name</Label>
                  <Input
                    type="text"
                    id="edit-lastName"
                    name="lastName"
                    defaultValue={selectedEmployee.lastName}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="edit-email">Login Email Address</Label>
                <Input
                  type="email"
                  id="edit-email"
                  name="email"
                  defaultValue={selectedEmployee.email}
                  required
                />
                <Hint>
                  Changing this signs the employee out and invalidates the previous login link. Send a new invitation after saving.
                </Hint>
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="edit-department">Department</Label>
                  <Input
                    type="text"
                    id="edit-department"
                    name="department"
                    defaultValue={selectedEmployee.department || ''}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="edit-position">Position</Label>
                  <Input
                    type="text"
                    id="edit-position"
                    name="position"
                    defaultValue={selectedEmployee.position || ''}
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="edit-employeeCode">Danløn Medarb. nr.</Label>
                  <Input
                    type="text"
                    id="edit-employeeCode"
                    name="employeeCode"
                    defaultValue={selectedEmployee.employeeCode || ''}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="edit-startDate">Start Date</Label>
                  <Input
                    type="date"
                    id="edit-startDate"
                    name="startDate"
                    disabled={!localActions}
                    defaultValue={selectedEmployee.startDate ? selectedEmployee.startDate.slice(0, 10) : ''}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="edit-cpr">CPR</Label>
                <Input
                  type="text"
                  id="edit-cpr"
                  name="cpr"
                  defaultValue={selectedEmployee.cpr || ''}
                  placeholder="DDMMYY-XXXX"
                />
                <Hint>Danish CPR number — 10 digits (DDMMYY-XXXX)</Hint>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="edit-hourlyRateEur">Timeløn (EUR)</Label>
                <Input
                  type="number"
                  id="edit-hourlyRateEur"
                  name="hourlyRateEur"
                  step="0.01"
                  min="0"
                  defaultValue={selectedEmployee.hourlyRateEur !== null && selectedEmployee.hourlyRateEur !== undefined ? selectedEmployee.hourlyRateEur : ''}
                />
                <Hint>Hourly wage in EUR — used for the informational Bruttoløn on the Payroll page.</Hint>
              </FormGroup>

              {/* Preferred UI language — applied on the employee's next login */}
              <FormGroup>
                <Label htmlFor="edit-language">Preferred language</Label>
                <FormSelect
                  id="edit-language"
                  name="language"
                  defaultValue={selectedEmployee.language || ''}
                >
                  <option value="">(Use browser default)</option>
                  {languagesForRole('employee').map((code) => (
                    <option key={code} value={code}>{LANGUAGES[code] ? LANGUAGES[code].nativeName : code}</option>
                  ))}
                </FormSelect>
                <Hint>The employee sees the app in this language when they log in; they can change it in their profile.</Hint>
              </FormGroup>

              <FormActions>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update Employee
                </Button>
              </FormActions>
            </form>
          </FormContainer>
        </Modal>
      )}
      
      {/* Assign Customer Modal (one customer per employee) */}
      {showAssignCustomersModal && selectedEmployee && (
        <Modal
          title={`Manage placement for ${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
          onClose={() => setShowAssignCustomersModal(false)}
          size="lg"
        >
          <FormContainer>
            {assignError && (
              <AlertMessage type="error">
                {assignError}
              </AlertMessage>
            )}
            <DetailSection>
              <DetailTitle>Select Customer</DetailTitle>
              <PlacementNote>
                An employee can have one current placement. Choosing another customer is a reassignment:
                the current placement is ended and a new one starts, while the old placement remains in history.
                Choosing “No customer” ends the current placement without deleting it.
              </PlacementNote>
              <CustomerList>
                <CustomerCheckboxLabel>
                  <input
                    type="radio"
                    name="assign-customer"
                    checked={selectedCustomerId === ''}
                    onChange={() => setSelectedCustomerId('')}
                  />
                  No customer (unassigned)
                </CustomerCheckboxLabel>
                {customers.length > 0 ? (
                  customers.map(customer => (
                    <CustomerCheckboxLabel key={customer.id}>
                      <input
                        type="radio"
                        name="assign-customer"
                        checked={selectedCustomerId === customer.id}
                        onChange={() => setSelectedCustomerId(customer.id)}
                      />
                      {customer.name}
                    </CustomerCheckboxLabel>
                  ))
                ) : (
                  <p>No active customers available for assignment.</p>
                )}
              </CustomerList>
            </DetailSection>

            <DetailSection>
              <DetailTitle>Placement history</DetailTitle>
              <PlacementHistory
                placements={getPlacements(selectedEmployee)}
                customers={customers}
                emptyMessage="No previous placements."
                showGuidance={false}
              />
            </DetailSection>

            <FormActions>
              <Button
                variant="outline"
                onClick={() => setShowAssignCustomersModal(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleAssignCustomers}>
                Save Placement
              </Button>
            </FormActions>
          </FormContainer>
        </Modal>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <Modal
          title="Add New Employee"
          onClose={() => setShowAddModal(false)}
        >
          <FormContainer>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);

              // CPR is optional, but when present must be exactly 10 digits.
              const cprInput = (formData.get('cpr') || '').trim();
              const cprDigits = cprInput.replace(/\D/g, '');
              if (cprInput && cprDigits.length !== 10) {
                setAlert({
                  type: 'error',
                  message: 'CPR must be 10 digits (DDMMYY-XXXX).'
                });
                return;
              }

              // Hourly wage in EUR — optional; omitted when the field is empty.
              const hourlyRateInput = (formData.get('hourlyRateEur') || '').trim();

              const data = {
                firstName: formData.get('firstName'),
                lastName: formData.get('lastName'),
                email: formData.get('email'),
                department: formData.get('department'),
                position: formData.get('position'),
                employeeCode: formData.get('employeeCode'),
                ...(localActions ? { startDate: formData.get('startDate') || null } : {}),
                cpr: cprDigits,
                language: formData.get('language') || null,
                ...(hourlyRateInput !== '' && { hourlyRateEur: Number(hourlyRateInput) })
              };
              handleCreateEmployee(data);
            }}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="add-firstName">First Name</Label>
                  <Input
                    type="text"
                    id="add-firstName"
                    name="firstName"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="add-lastName">Last Name</Label>
                  <Input
                    type="text"
                    id="add-lastName"
                    name="lastName"
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="add-email">Email Address</Label>
                <Input
                  type="email"
                  id="add-email"
                  name="email"
                  required
                />
                <Hint>Send Invitations emails a personal login link. An active employee can also request a login email from the Tempus homepage.</Hint>
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="add-department">Department</Label>
                  <Input
                    type="text"
                    id="add-department"
                    name="department"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="add-position">Position</Label>
                  <Input
                    type="text"
                    id="add-position"
                    name="position"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="add-employeeCode">Danløn Medarb. nr.</Label>
                  <Input
                    type="text"
                    id="add-employeeCode"
                    name="employeeCode"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="add-startDate">Start Date</Label>
                  <Input
                    type="date"
                    id="add-startDate"
                    name="startDate"
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="add-cpr">CPR</Label>
                <Input
                  type="text"
                  id="add-cpr"
                  name="cpr"
                  placeholder="DDMMYY-XXXX"
                />
                <Hint>Danish CPR number — 10 digits (DDMMYY-XXXX)</Hint>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="add-hourlyRateEur">Timeløn (EUR)</Label>
                <Input
                  type="number"
                  id="add-hourlyRateEur"
                  name="hourlyRateEur"
                  step="0.01"
                  min="0"
                />
                <Hint>Hourly wage in EUR — used for the informational Bruttoløn on the Payroll page.</Hint>
              </FormGroup>

              {/* Preferred UI language — applied on the employee's first login */}
              <FormGroup>
                <Label htmlFor="add-language">Preferred language</Label>
                <FormSelect id="add-language" name="language" defaultValue="">
                  <option value="">(Use browser default)</option>
                  {languagesForRole('employee').map((code) => (
                    <option key={code} value={code}>{LANGUAGES[code] ? LANGUAGES[code].nativeName : code}</option>
                  ))}
                </FormSelect>
                <Hint>The employee sees the app in this language when they log in; they can change it in their profile.</Hint>
              </FormGroup>

              <FormActions>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create Employee
                </Button>
              </FormActions>
            </form>
          </FormContainer>
        </Modal>
      )}

      {/* Employee Created Info Modal */}
      {createdInfo && (
        <Modal
          title="Employee Created"
          onClose={() => setCreatedInfo(null)}
        >
          <p>
            {createdInfo.name
              ? `${createdInfo.name} was created successfully.`
              : 'The employee was created successfully.'}
          </p>
          <p>
            To give them access, select the employee in the list and use{' '}
            <strong>Send Invitations</strong> — they receive an e-mail with a personal login link.
            {' '}An active employee can also enter their registered email on the Tempus homepage to request access.
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setCreatedInfo(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}

      {/* Danløn import report */}
      {importReport && (
        <Modal title="Danløn import" size="lg" onClose={() => setImportReport(null)}>
          <p>
            Processed <strong>{importReport.totalRows}</strong> rows from the Danløn medarbejderoversigt
            {importReport.uniqueEmployees != null && importReport.uniqueEmployees !== importReport.totalRows && (
              <> — collapsed to <strong>{importReport.uniqueEmployees}</strong> unique employees (the same person on multiple Danløn profiles is merged by CPR)</>
            )}.
          </p>
          <ul style={{ lineHeight: 1.8 }}>
            <li><strong>{importReport.updated?.length || 0}</strong> Tempus employees updated from Danløn (Medarb.nr / CPR / name / start date)</li>
            <li>
              <strong>{importReport.createdInTempus?.length || 0}</strong> created in Tempus (in Danløn, not in Tempus) — created inactive
              {importReport.createdWithPlaceholder > 0 && (
                <> , of which <strong>{importReport.createdWithPlaceholder}</strong> got a placeholder e-mail (no e-mail in Danløn)</>
              )}
            </li>
            <li><strong>{importReport.unchanged || 0}</strong> already up to date</li>
          </ul>

          {importReport.toCreateInDanlon?.length > 0 && (
            <>
              <h4 style={{ marginTop: '16px', color: '#b9770e' }}>Create these in Danløn ({importReport.toCreateInDanlon.length})</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--ve-ink-600)' }}>
                In Tempus with a CPR, unknown to Danløn. Create them in Danløn; Danløn assigns a Medarb.nr that syncs back on the next import.
              </p>
              <div style={{ maxHeight: '160px', overflowY: 'auto', fontSize: '0.85rem' }}>
                {importReport.toCreateInDanlon.map((r, i) => (
                  <div key={i}>{r.name} — CPR {r.cpr} — {r.email}</div>
                ))}
              </div>
            </>
          )}

          {importReport.emailMissingInDanlon?.length > 0 && (
            <>
              <h4 style={{ marginTop: '16px', color: '#b9770e' }}>Update e-mail in Danløn ({importReport.emailMissingInDanlon.length})</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--ve-ink-600)' }}>Present in Tempus, missing in Danløn — add the e-mail in Danløn.</p>
              <div style={{ maxHeight: '140px', overflowY: 'auto', fontSize: '0.85rem' }}>
                {importReport.emailMissingInDanlon.map((r, i) => (
                  <div key={i}>{r.name} (Medarb.nr {r.medarbNr || '—'}) — {r.email}</div>
                ))}
              </div>
            </>
          )}

          {importReport.errors?.length > 0 && (
            <>
              <h4 style={{ marginTop: '16px', color: 'var(--act-danger)' }}>Skipped / errors ({importReport.errors.length})</h4>
              <div style={{ maxHeight: '140px', overflowY: 'auto', fontSize: '0.85rem', color: 'var(--act-danger)' }}>
                {importReport.errors.map((r, i) => (
                  <div key={i}>{r.message}</div>
                ))}
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setImportReport(null)}>Close</Button>
          </div>
        </Modal>
      )}

      {/* Invitation result */}
      {inviteReport && (
        <Modal title="Invitations" size="lg" onClose={() => { setInviteReport(null); setCopiedIdx(null); }}>
          <p>
            Processed <strong>{inviteReport.total}</strong> invitation(s) —
            {' '}<strong>{inviteReport.queued || 0}</strong> queued for delivery
            {inviteReport.rateLimit ? ` (mail limit ${inviteReport.rateLimit.limit} per ${inviteReport.rateLimit.windowMinutes} min)` : ''}.
            {!inviteReport.mailConfigured && (
              <> SMTP is not configured, so no e-mails were sent. Configure SMTP and retry; employee access has not changed.</>
            )}
          </p>
          {inviteBatchId && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '0.9rem' }}>
              <span>
                <strong>{inviteCounts.sent}</strong> sent · <strong>{inviteCounts.queued}</strong> waiting · <strong>{inviteCounts.failed}</strong> failed
                {inviteBatch && inviteBatch.complete ? ' — done' : ''}
                {inviteBatch && !inviteBatch.complete && inviteBatch.estimatedMinutes > 0 ? ` — about ${inviteBatch.estimatedMinutes} min to go` : ''}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/email?batchId=${encodeURIComponent(inviteBatchId)}`)}
              >
                <FiMail style={{ marginRight: '6px' }} />
                Open E-mail delivery
              </Button>
            </div>
          )}
          <div style={{ maxHeight: '340px', overflowY: 'auto', fontSize: '0.85rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--ve-ink-600)' }}>
                  <th style={{ padding: '4px 6px' }}>Name</th>
                  <th style={{ padding: '4px 6px' }}>E-mail</th>
                  <th style={{ padding: '4px 6px' }}>Result</th>
                  <th style={{ padding: '4px 6px' }}>Login link</th>
                </tr>
              </thead>
              <tbody>
                {(inviteReport.results || []).map((r, i) => {
                  // Live outbox status once the batch poll has it; null when
                  // the mail was never queued (placeholder / link relayed).
                  const delivery = r.ok === false ? null : describeDelivery(r, inviteBatch);
                  return (
                  <tr key={i} style={{ borderTop: '1px solid var(--surface-subtle)' }}>
                    <td style={{ padding: '4px 6px' }}>{r.name || '—'}</td>
                    <td style={{ padding: '4px 6px', wordBreak: 'break-all' }}>{r.email}</td>
                    <td style={{ padding: '4px 6px', color: r.ok === false ? 'var(--act-danger)' : delivery ? DELIVERY_COLORS[delivery.tone] : '#b9770e' }}>
                      {r.ok === false ? (r.message || 'error')
                        : delivery ? delivery.text
                        : r.placeholder ? 'no real e-mail — set one first'
                        : 'not e-mailed'}
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      {r.loginLink ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            readOnly
                            value={r.loginLink}
                            onFocus={(e) => e.target.select()}
                            style={{
                              flex: 1,
                              minWidth: '180px',
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              padding: '2px 4px',
                              border: '1px solid #d5d8dc',
                              borderRadius: '4px'
                            }}
                          />
                          <ActionButton
                            type="button"
                            variant="icon"
                            title={copiedIdx === i ? 'Copied' : 'Copy login link'}
                            onClick={() => handleCopyLoginLink(r.loginLink, i)}
                          >
                            {copiedIdx === i ? <FiCheck /> : <FiCopy />}
                          </ActionButton>
                        </div>
                      ) : ''}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => { setInviteReport(null); setCopiedIdx(null); }}>Close</Button>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Employees;
